/**
 * Delivery 24h landing: on a product page opened with ?w24=1, sizes whose variant is not flagged
 * custom.wysylka_24h (data-wysylka-24h="false" on the size radio) are shown disabled with a note.
 * Without w24=1 this file does nothing.
 *
 * Only the size option is touched (radios whose data-option-position matches the size fieldset).
 * The currently checked size is never disabled, so a hand-edited ?variant= pointing at a non-24h size
 * together with w24=1 stays selectable (accepted edge case).
 *
 * Invariant: in w24 mode the main product form can only be submitted with a checked size flagged
 * data-wysylka-24h="true" (see the capture `submit` guard below).
 *
 * The theme replaces the whole <variant-picker> on every selection: it dispatches `product:rerender`
 * on the form (non-bubbling, so it is caught in the capture phase on document) and `variant:change`.
 * State is re-applied one frame later, after the theme has swapped the markup.
 */
(() => {
  if (window.__delivery24hSizesBound) return;

  if (new URLSearchParams(window.location.search).get('w24') !== '1') return;

  window.__delivery24hSizesBound = true;

  const script = document.currentScript;
  const config = {
    css: (script && script.dataset.css) || '',
    note: (script && script.dataset.note) || '',
    info: (script && script.dataset.info) || '',
    blocked: (script && script.dataset.blocked) || '',
    blockedLink: (script && script.dataset.blockedLink) || ''
  };

  if (config.css && !document.querySelector(`link[href="${config.css}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = config.css;
    document.head.appendChild(link);
  }

  const getLabel = (radio) => {
    return document.querySelector(`label[for="${radio.id}"]`) || radio.closest('label');
  };

  const applyToRadio = (radio) => {
    if (radio.getAttribute('data-wysylka-24h') !== 'false' || radio.checked || radio.hasAttribute('data-w24-disabled')) {
      return;
    }

    radio.disabled = true;
    radio.setAttribute('data-w24-disabled', '');

    const label = getLabel(radio);
    if (!label) return;

    label.classList.add('is-disabled', 'w24-unavailable');

    if (!label.querySelector('.w24-note')) {
      const note = document.createElement('span');
      note.id = `${radio.id}-w24-note`;
      note.className = 'w24-note text-xs';
      note.textContent = config.note;
      label.appendChild(note);
      radio.setAttribute('aria-describedby', note.id);
    }
  };

  const apply = () => {
    document.querySelectorAll('variant-picker[context="main_product"]').forEach((picker) => {
      const formId = picker.getAttribute('form-id');
      const fieldset = picker.querySelector('fieldset[data-option-role="size"]');
      if (!fieldset) return;

      const position = fieldset.dataset.optionPosition;

      // Radios are looked up by form (not DOM position): the popover is moved to <body> while open.
      const radios = Array.from(document.querySelectorAll('input[type="radio"][data-wysylka-24h]')).filter((radio) => {
        return radio.getAttribute('form') === formId && radio.getAttribute('data-option-position') === position;
      });

      radios.forEach(applyToRadio);

      if (radios.some((radio) => radio.hasAttribute('data-w24-disabled')) && !fieldset.querySelector('.w24-info')) {
        const info = document.createElement('p');
        info.className = 'w24-info text-sm text-subdued';
        info.textContent = config.info;
        fieldset.appendChild(info);
      }
    });
  };

  const getSizeContext = (formId) => {
    const picker = document.querySelector(`variant-picker[context="main_product"][form-id="${formId}"]`);
    const fieldset = picker && picker.querySelector('fieldset[data-option-role="size"]');
    if (!fieldset) return null;

    const position = fieldset.dataset.optionPosition;
    const radios = Array.from(document.querySelectorAll('input[type="radio"][data-wysylka-24h]')).filter((radio) => {
      return radio.getAttribute('form') === formId && radio.getAttribute('data-option-position') === position;
    });

    return { fieldset, radios };
  };

  const clearBlocked = () => {
    document.querySelectorAll('.w24-blocked').forEach((element) => element.remove());
  };

  const showBlocked = (fieldset, formId, hasCheckedSize) => {
    let message = fieldset.querySelector('.w24-blocked');

    if (!message) {
      message = document.createElement('p');
      message.id = `w24-blocked-${formId}`;
      message.className = 'w24-blocked text-sm';
      message.setAttribute('role', 'alert');
      message.append(config.blocked);

      if (hasCheckedSize && config.blockedLink) {
        // Same page and params, minus w24: the regular (made-to-order) purchase path.
        const url = new URL(window.location.href);
        url.searchParams.delete('w24');

        const link = document.createElement('a');
        link.href = url.toString();
        link.textContent = config.blockedLink;
        message.append(' ', link);
      }

      fieldset.appendChild(message);
    }

    const rect = message.getBoundingClientRect();
    if (rect.top < 100 || rect.bottom > window.innerHeight) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      fieldset.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }
  };

  // Capture on document (also covers the sticky bar button, which submits the main form via form=).
  // Registered after the select-size guard of variant-picker.liquid, which stops propagation first when
  // no size is checked and 2+ sizes are choosable; we skip that case too, so only one message is ever shown.
  const onSubmit = (event) => {
    // getAttribute: the form's hidden <input name="id"> shadows the form.id property.
    const formId = event.target && event.target.getAttribute && event.target.getAttribute('id');
    if (!formId) return;

    const context = getSizeContext(formId);
    if (!context || !context.radios.length) return;

    const checked = context.radios.find((radio) => radio.checked);
    if (checked && checked.getAttribute('data-wysylka-24h') === 'true') return;

    const choosable = context.radios.filter((radio) => !radio.hasAttribute('data-w24-disabled'));
    if (!checked && choosable.length > 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showBlocked(context.fieldset, formId, Boolean(checked));
  };

  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      apply();
    });
  };

  const start = () => {
    apply();
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('change', (event) => {
      if (event.target && event.target.type === 'radio') clearBlocked();
    });
    document.addEventListener('product:rerender', () => {
      clearBlocked();
      schedule();
    }, true);
    document.addEventListener('variant:change', schedule);
    document.addEventListener('shopify:section:load', schedule);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
